// App.js
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import Home from "./components/Home/Home";
import { UserAuthContextProvider, useUserAuth } from "./context/UserAuthContext";
// The live RSVP form is retired now that the wedding has happened. Rsvp.js is
// kept on disk: swap this import and the /rsvp route back to restore it.
import RsvpClosed from "./components/RSVP/RsvpClosed";
import BabyReveal from "./components/BabyReveal/BabyReveal";
import Default from "./components/Default/Default";
import TravelStay from "./components/TravelStay/TravelStay";
import Registry from "./components/Registry/Registry";
import Login from "./components/Auth/Login";
import PhoneLogin from "./components/Auth/PhoneLogin";
import TermsPolicy from "./components/Auth/TermsPolicy";
import UserProfile from "./components/Profile/UserProfile";
import DeleteAccount from "./components/Profile/DeleteAccount";
import ProfileCompletionGuide from "./components/Profile/ProfileCompletionGuide";
import ResetProfileGuide from "./components/Profile/ResetProfileGuide";
import PhotoGallery from "./components/Photos/PhotoGallery";
import PhotoUpload from "./components/Photos/PhotoUpload";
import TopPhotos from "./components/Photos/TopPhotos";
import WishesList from "./components/Wishes/WishesList";
import Dashboard from "./components/Admin/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import "./App.css";
import "./styles/icon-styles.css";

export const API_BASE_URI = process.env.REACT_APP_API_BASE_URI;

// This component will manage global route-based styling
const RouteStyleManager = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
        // Check if current path is homepage
        const isHomepage = location.pathname === '/' || location.pathname === '';

        // Routes that should have the blue background
        const blueBackgroundRoutes = ['/travel', '/registry', '/photos', '/wishes', '/rsvp', '/profile'];

        // Check if current path should have blue background
        const shouldHaveBlueBackground = blueBackgroundRoutes.some(route =>
            location.pathname.startsWith(route)
        );

        // Apply styles to body
        if (isHomepage) {
            document.body.classList.add('homepage');
            document.documentElement.classList.add('homepage');
            document.body.classList.remove('blue-background');
            document.documentElement.classList.remove('blue-background');
        } else if (shouldHaveBlueBackground) {
            document.body.classList.add('blue-background');
            document.documentElement.classList.add('blue-background');
            document.body.classList.remove('homepage');
            document.documentElement.classList.remove('homepage');
        } else {
            document.body.classList.remove('blue-background');
            document.documentElement.classList.remove('blue-background');
            document.body.classList.remove('homepage');
            document.documentElement.classList.remove('homepage');
        }

        return () => {
            // Cleanup
            document.body.classList.remove('blue-background');
            document.documentElement.classList.remove('blue-background');
            document.body.classList.remove('homepage');
            document.documentElement.classList.remove('homepage');
        };
    }, [location.pathname]);

    return children;
};

// Component to handle ProfileCompletionGuide display
const ProfileGuideManager = ({ children }) => {
    const { user, userProfile, loading } = useUserAuth();
    const [showProfileGuide, setShowProfileGuide] = useState(false);
    const location = useLocation();

    useEffect(() => {
        // Don't show anything while auth is loading
        if (loading) {
            return;
        }

        // Check if we should show the welcome guide for all visitors
        const shouldShowGuide = () => {
            // Don't show on login/auth pages
            if (location.pathname.includes('/login') || location.pathname.includes('/phone-login')) {
                return false;
            }
            
            // Don't show if already shown in this session
            if (sessionStorage.getItem('welcomeGuideShown') === 'true') {
                return false;
            }
            
            // Show for all visitors on first visit (both guests and users)
            return true;
        };

        // Show guide after a short delay to ensure everything is loaded
        const timer = setTimeout(() => {
            if (shouldShowGuide()) {
                setShowProfileGuide(true);
                // Don't mark as shown until the user closes the modal
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [location.pathname, loading]);

    const handleCloseProfileGuide = () => {
        setShowProfileGuide(false);
        // Mark as shown in session storage when user closes the modal
        sessionStorage.setItem('welcomeGuideShown', 'true');
    };

    return (
        <>
            {children}
            <ProfileCompletionGuide 
                show={showProfileGuide} 
                onHide={handleCloseProfileGuide}
            />
        </>
    );
};

function App() {
    return (
        <UserAuthContextProvider>
            <RouteStyleManager>
                <ProfileGuideManager>
                    <Header />
                    <main>
                        <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<Home/>}/>
                        <Route path="/registry" element={<Registry />} />
                        <Route path="/travel" element={<TravelStay />} />
                        <Route path="/rsvp" element={<RsvpClosed />} />
                        <Route path="/baby-reveal" element={<BabyReveal />} />
                        <Route path="/our-story" element={<Default />} />
                        <Route path="/details" element={<Default />} />

                        {/* Auth Routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/phone-login" element={<PhoneLogin />} />
                        <Route path="/terms-policy" element={<TermsPolicy />} />

                        {/* Partially protected routes - viewing is public, actions require auth */}
                        <Route path="/photos" element={<PhotoGallery />} />
                        <Route path="/photos/top" element={<TopPhotos />} />
                        <Route path="/wishes" element={<WishesList />} />

                        {/* Protected Routes - require authentication */}
                        <Route path="/photos/upload" element={
                            <ProtectedRoute>
                                <PhotoUpload />
                            </ProtectedRoute>
                        } />
                        
                        <Route path="/profile" element={
                            <ProtectedRoute>
                                <UserProfile />
                            </ProtectedRoute>
                        } />
                        
                        <Route path="/profile/delete-account" element={
                            <ProtectedRoute>
                                <DeleteAccount />
                            </ProtectedRoute>
                        } />

                        {/* Admin Routes - require admin role */}
                        <Route path="/admin/*" element={
                            <AdminRoute>
                                <Dashboard />
                            </AdminRoute>
                        } />

                        {/* Redirects */}
                        <Route path="/phonesignup" element={<Navigate to="/phone-login" replace />} />

                        {/* Fallback for unknown routes */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </main>
                    <Footer/>
                </ProfileGuideManager>
                <ResetProfileGuide />
            </RouteStyleManager>
        </UserAuthContextProvider>
    );
}

export default App;